<script setup>
import Dropdown from '~/components/UI/Dropdown.vue'
import UserMenu from '~/components/Common/UserMenu.vue'
import ActingAsBadge from '~/components/Common/ActingAsBadge.vue'
import BugReportButton from '~/components/Common/BugReportButton.vue'

const route = useRoute()
const {can, actions, subjects} = useRolePermissions()
const authStore = useAuthStore()
const configStore = useConfigStore()
const { bugReport } = useFeatureFlags()
const {isPromotionSection, navItems, promotions, selectedPromotion, openPromotion, reloadPromotions, isNavItemActive} = usePromotionNavigation()
const {toggle: toggleSubmenu} = usePromotionSubmenu()

const appName = computed(() => configStore.appName)

const mainMenuConfig = [
  {name: 'Dashboard', icon: 'heroicons:home', path: '/', permission: [actions.VIEW, subjects.DASHBOARD]},
  {
    name: 'Ofertas',
    icon: 'material-symbols:sell',
    path: '/quotes',
    permission: [actions.LIST, subjects.BUDGET_QUOTE],
    roleOnly: ['contractor_admin', 'contractor_user']
  },
  {
    name: 'Promociones',
    icon: 'heroicons:building-office',
    path: '/promotions',
    permission: [actions.LIST, subjects.PROMOTIONS],
    isPromotionOverlay: true
  },
  {
    name: 'Central de Compras',
    icon: 'mdi:shopping',
    path: '/product-items',
    permission: [actions.LIST, subjects.PRODUCT_ITEMS]
  },
  {name: 'Empresas', icon: 'material-symbols:handshake', path: '/companies', permission: [actions.LIST, subjects.COMPANIES]},
  {name: 'Usuarios', icon: 'heroicons:user-group', path: '/users', permission: [actions.LIST, subjects.USERS]},
  {
    name: 'Invitaciones',
    icon: 'mdi:email-fast-outline',
    path: '/invitations',
    permission: [actions.LIST, subjects.USER_INVITATION],
    hideForRoles: ['contractor_admin', 'contractor_user']
  },
  {name: 'Configuración', icon: 'heroicons:cog-6-tooth', path: '/settings', roleOnly: ['architect', 'contractor_admin']},
]

const menuItems = computed(() => {
  return mainMenuConfig.filter(item => {
    // Filtrar por rol si existe roleOnly
    if (item.roleOnly && !item.roleOnly.includes(authStore.role)) {
      return false
    }
    if (item.hideForRoles?.includes(authStore.role)) {
      return false
    }
    // Filtrar por permisos si existe
    if (!item.permission) return true
    return can(item.permission[0], item.permission[1])
  })
})

provide('reloadPromotions', reloadPromotions)

const isMenuItemActive = (itemPath) => {
  const currentPath = route.path
  // Para la ruta raíz, solo activar si es exactamente "/"
  if (itemPath === '/') {
    return currentPath === '/'
  }
  // Para otras rutas, activar si la ruta actual comienza con el path del item
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/')
}

const handlePromotionClick = () => {
  if (isPromotionSection.value) {
    toggleSubmenu()
  } else {
    navigateTo('/promotions')
  }
}
</script>

<template>
  <aside class="flex-shrink-0 hidden md:flex h-full">
    <div class="w-20 bg-[#0f172a] text-slate-400 flex flex-col items-center z-20">
      <div class="flex p-3">
        <div class="flex p-2 rounded bg-gray-700">
          <img :src="'/images/logo_small.png'" :alt="appName" class="h-6">
        </div>
      </div>
      <nav class="flex-1 p-2 space-y-2">
        <template v-for="item in menuItems" :key="item.path">
          <template v-if="item.isPromotionOverlay">
            <button
              type="button"
              v-tooltip.right="item.name"
              class="md:flex lg:hidden w-full items-center justify-center p-3 rounded-lg transition-colors hover:bg-gray-700 group"
              :class="{ 'bg-gray-700 hover:bg-gray-600': isMenuItemActive(item.path) }"
              @click="handlePromotionClick"
            >
              <Icon :name="item.icon" class="text-xl"/>
            </button>
            <NuxtLink
              v-tooltip.right="item.name"
              :to="item.path"
              class="hidden lg:flex items-center justify-center p-3 rounded-lg transition-colors hover:bg-gray-700 group"
              :class="{ 'bg-gray-700 hover:bg-gray-600': isMenuItemActive(item.path) }"
            >
              <Icon :name="item.icon" class="text-xl"/>
            </NuxtLink>
          </template>

          <NuxtLink
            v-else
            v-tooltip.right="item.name"
            :to="item.path"
            class="flex items-center justify-center p-3 rounded-lg transition-colors hover:bg-gray-700 group"
            :class="{ 'bg-gray-700 hover:bg-gray-600': isMenuItemActive(item.path) }"
          >
            <Icon :name="item.icon" class="text-xl"/>
          </NuxtLink>
        </template>
      </nav>

      <BugReportButton v-if="bugReport.enabled" />

      <UserMenu :minimal="true"/>

    </div>

    <div
        v-if="isPromotionSection"
        class="hidden lg:flex w-64 border-r border-slate-200 bg-white flex-col justify-between px-4 pt-4 pb-2"
    >
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-2 relative">
          <span class="text-xs font-bold text-slate-400 uppercase tracking-wider px-2 mb-2">Promoción Activa</span>
          <Dropdown v-model="selectedPromotion" :options="promotions" @selected="openPromotion">
            <template #selected="{selected}">
              <h2 class="text-sm font-bold text-slate-900 truncate">
                {{ selected.name }}
              </h2>
              <p class="text-[10px] text-slate-500 truncate">
                {{ selected.address?.city || 'Sin ubicación' }}
              </p>
            </template>
            <template #option="{option}">
              <h3 class="text-sm font-bold text-slate-900 truncate">
                {{ option.name }}
              </h3>
              <p class="text-[10px] text-slate-500 truncate">
                {{ option.address?.city || 'Sin ubicación' }}
              </p>
            </template>
          </Dropdown>

          <ActingAsBadge class="mt-2 px-2"/>
        </div>

        <nav class="flex flex-col gap-1.5">
          <template v-for="(item, index) in navItems" :key="index">
            <p v-if="item.section" class="text-xs font-bold text-slate-400 uppercase tracking-wider px-1 mt-2 mb-1">
              {{ item.section }}
            </p>
            <NuxtLink
                v-else :to="item.to"
                class="flex items-center gap-3 px-1 py-2 rounded transition-colors group"
                :class="isNavItemActive(item) ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-600 hover:bg-slate-100'">
              <div class="relative flex">
                <Icon :name="item.icon" class="text-[20px] group-hover:scale-105 transition-transform"/>
                <span
                    v-if="item.badge"
                    class="absolute -top-0.5 -right-0.5 size-2 bg-red-500 rounded-full border-2 border-white"/>
              </div>
              <p class="text-sm font-medium">
                {{ item.label }}
              </p>
            </NuxtLink>
          </template>
        </nav>
      </div>
    </div>

  </aside>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
